import { useEffect, useRef, useState } from 'react';

/** The query parameter the report page's aside deep links use. */
const ASIDE_PARAM = 'aside';

/** The `aside` deep-link value that opens Armadillo's conversation. */
export const ARMADILLO_ASIDE = 'armadillo';

/** With `?aside=armadillo`, what the conversation's composer answers first:
 * `<orderId>:<messageId>` for one of the research desk's emails, or an
 * order id alone for the latest email on that order still waiting. */
export const ARMADILLO_TARGET_PARAM = 'target';

/** The `target` value naming one of the desk's emails, or, without a
 * Message-ID, the order it is on. The same key the conversation's reply
 * targets carry. */
export function armadilloReplyTargetKey(orderId: string, messageId?: string | null): string {
  return messageId ? `${orderId}:${messageId}` : orderId;
}

/** The target an `?aside=armadillo` link names; null without the link, or
 * without a target on it. */
export function armadilloReplyTargetOf(params: URLSearchParams): string | null {
  if (params.get(ASIDE_PARAM) !== ARMADILLO_ASIDE) {
    return null;
  }
  return params.get(ARMADILLO_TARGET_PARAM) || null;
}

/** The search string that opens Armadillo's conversation on `target` (or
 * on its default, for null), keeping the other parameters of `base`. */
export function armadilloAsideSearch(target: string | null, base?: URLSearchParams): string {
  const next = new URLSearchParams(base);
  next.set(ASIDE_PARAM, ARMADILLO_ASIDE);
  if (target) {
    next.set(ARMADILLO_TARGET_PARAM, target);
  } else {
    next.delete(ARMADILLO_TARGET_PARAM);
  }
  return `?${next.toString()}`;
}

interface ArmadilloAsideLinkOptions {
  /** The page was opened with `?aside=armadillo`. */
  requested: boolean;
  /** The link's `target`, when it names one (`armadilloReplyTargetOf`). */
  target?: string | null;
  /** The Partners tab exists here: an admin, not in print mode. */
  enabled: boolean;
  /** The report's assignments have loaded: only they say whether Armadillo
   * is listed, so the link waits for them. */
  assignmentsLoaded: boolean;
  /** Armadillo is among the report's listed partners. */
  hasRegistryPartner: boolean;
  setAsideTab?: (tab: string | null) => void;
  setAsideOpen?: (open: boolean) => void;
}

export interface ArmadilloAsideLanding {
  /** The aside should land on Chat → Partners → Armadillo. */
  landed: boolean;
  /** What the conversation answers first, as the link named it. */
  target: string | null;
}

/**
 * The report page's `?aside=armadillo` deep link (the Slack ping, the
 * registry inbox items, and the Reply controls on the orders card and the
 * admin page): once the report's assignments have loaded, opens the aside
 * on Chat → Partners → Armadillo. Without Armadillo listed it does
 * nothing: the orders card on the page is where its mail is read.
 *
 * One-shot per target, like the page's other aside links: a later change
 * to the assignments must not yank the analyst back after they move on,
 * but a Reply control naming another email (a new `target`) opens the
 * conversation again on that one.
 */
export function useArmadilloAsideLink({
  requested,
  target = null,
  enabled,
  assignmentsLoaded,
  hasRegistryPartner,
  setAsideTab,
  setAsideOpen,
}: ArmadilloAsideLinkOptions): ArmadilloAsideLanding {
  // The target of the link last acted on; '' for a link without one.
  const consumedRef = useRef<string | null>(null);
  const [landing, setLanding] = useState<ArmadilloAsideLanding>({ landed: false, target: null });

  useEffect(() => {
    if (!requested || !enabled || !assignmentsLoaded) {
      return;
    }
    const key = target ?? '';
    if (consumedRef.current === key) {
      return;
    }
    consumedRef.current = key;
    if (!hasRegistryPartner) {
      return;
    }
    setAsideTab?.('chat');
    setAsideOpen?.(true);
    setLanding({ landed: true, target });
  }, [
    requested,
    target,
    enabled,
    assignmentsLoaded,
    hasRegistryPartner,
    setAsideTab,
    setAsideOpen,
  ]);

  return landing;
}
